import { powerupOnBoard } from './unlock-events';
import { isPowerupRetired, unlockPuzzleFor } from '../powerups/powerup-registry';
import { clearableFree } from '../solver/free-solver';
import { movementConstraintOf } from '../model/shape';
import { canStep, occupiedKeysExcept, slideAndExit, slideFullyAgainst } from '../model/free-move';
import { occupiedCells } from '../model/shape';
import { poweredPlateIds } from '../model/power';
import { boardExitOptions } from '../model/board-exit-options';
/**
 * How hard the solver may look for the move level 1's hand demonstrates.
 *
 * Inherited from the Hint feature removed in WP-43.1 — the budget outlived the
 * button because the measurement behind it is about `clearableFree`, not about
 * Hint, and this is now its only caller. **It used to be 200,000 nodes against
 * `solveFree`, and that was a defect.** Measured over levels 1–120 on the
 * WP-36.9 boards: the optimal BFS answered only **53 of them**, taking 597ms on
 * average and up to 1257ms of frozen main thread, peaking at **378MB** of
 * transient heap, because the search keeps one hash string per explored node.
 *
 * Raising the budget could not fix that: every search that DID succeed answered
 * inside 62k nodes, so the extra budget only bought a longer freeze before the
 * same `null`.
 *
 * A demonstration does not need the OPTIMAL move — it needs a move that does
 * not throw the level away. `clearableFree` proves exactly that, it is the same
 * search the generator already uses to accept every board it ships, and it
 * returns the first move of the clearing it finds. Measured on the same 120
 * levels: **120/120 answered, 1.0ms mean, 20ms worst, +62MB peak** — every
 * returned move legal on the real move model and solvability-preserving.
 */
const TUTORIAL_NODE_BUDGET = 10000;
/**
 * Module 52 (WP-52.5) — the caption for each power-up's lesson.
 *
 * ## Why this is a map and not a single id
 *
 * It used to be `const HAMMER_POWERUP_ID = 'hammer'`, commented "the first one
 * to unlock". That was true when it was written and stopped being true in
 * WP-50.26, which re-ordered the unlocks to run cheapest-first: Rainbow moved to
 * **20**, Hammer to **35**, Colour Delete to **50**. So the first power-up a
 * player is ever handed was the one with no lesson at all, and the second one
 * inherited the lesson written for "your first power-up"
 * (`Bugs/Rainbow Tutorial.mp4`).
 *
 * The rows are now derived from `config.unlocks.powerups` — the same object that
 * decides when the button unlocks — so adding a fourth power-up is a config entry
 * plus one string here, and an unlock level can never again drift away from the
 * lesson that teaches it.
 *
 * A power-up with no entry here simply gets no lesson. That is deliberate: the
 * alternative is a generic caption, and a hand pointing at a button under the
 * words "A power-up." teaches strictly less than nothing.
 */
const POWERUP_LESSONS = {
    rainbowGates: 'blockjam.tutorial.rainbowTap',
    hammer: 'blockjam.tutorial.hammerTap',
    colorDelete: 'blockjam.tutorial.colorDeleteTap',
    surge: 'blockjam.tutorial.surgeTap',
    // Module 67 (WP-67.B7) — the Gate Widener's lesson, first encounter at its
    // unlock ordinal (50) on a board with a widenable gate (`powerupOnBoard`).
    gateWidener: 'blockjam.tutorial.gateWidenerTap',
};
const ALL_DIRECTIONS = ['up', 'down', 'left', 'right'];
/**
 * Module 45 (WP-45.4) — a drag that takes a block **out through its own gate**,
 * or null if this board has none in a single slide.
 *
 * ## Why the solver's move was the wrong lesson
 *
 * WP-41.8 demonstrated `clearableFree(...).firstMove`, which is the first move
 * of a *clearing sequence* — and the first move of a sequence is very often a
 * **repositioning** move: shove the yellow block aside so the green one can
 * reach its gate later. That move is correct play and a terrible demonstration,
 * because the caption over it reads "Drag the block out through its matching
 * gate" while the hand drags a yellow block at a green gate. CrazyGames QA
 * reported exactly that on level 1 (BUG-003), and the videos show the hand
 * faithfully drawing the move it was given.
 *
 * So the level-1 hand asks a narrower question, which is also the question the
 * caption asks: *is there a block that, released in one direction, lands in a
 * gate of its own colour?* That is the whole mechanic in one gesture.
 *
 * ## Built from the model's predicates, not from a second copy of the rule
 *
 * `slideAndExit` is what a real drag does: release and the block travels until a
 * wall, another block, or a gate it can go through stops it. Re-deriving "does
 * this reach a gate" here would be a second implementation to keep in step with
 * the gate-span and axis-lock rules, and this file has no business knowing them.
 *
 * WP-50.4 moved this off `slideFully` + `exitingGate`, which asked whether the
 * block reached a gate at the END of its travel. The hand could therefore draw a
 * drag whose real outcome was different — it would demonstrate a block arriving
 * at a gate that, on the live board, had already left through a nearer one.
 *
 * Deterministic by construction: board order outside, a fixed direction order
 * inside, first hit wins. Level 1's board is seeded and identical for every
 * player (Module 35), so every new player now sees the identical lesson — which
 * is BUG-003's first acceptance criterion, and was NOT true of the solver, whose
 * answer depends on its own search order.
 */
function exitDemo(state) {
    const { blocks, puzzle } = state;
    for (const block of blocks) {
        for (const direction of ALL_DIRECTIONS) {
            // `null` for a block that cannot move that way at all — including one
            // already flush against that wall, which would otherwise resolve to a
            // zero-length "drag" that the board rightly refuses.
            //
            // No `rainbow` here on purpose: Rainbow Gates is a paid power-up unlocked
            // far later, and a lesson taught under its effect would be a lie about the
            // ordinary rules.
            // Module 63 — with the board's own exit options, so a demo never
            // promises an exit through a gate whose plate is dark.
            const outcome = slideAndExit(block, direction, blocks, puzzle.exits, puzzle.gridSize, boardExitOptions(state));
            if (!outcome?.exited)
                continue;
            return { gesture: 'drag', blockId: block.id, direction, exit: true };
        }
    }
    return null;
}
/**
 * The fallback: a solver move, with a caption that describes a solver move.
 *
 * Every board the generator ships is solvable, but not every board offers a
 * one-drag exit on its very first move — a tightly-packed opening may need a
 * block moved aside first. Refusing to teach anything there would be worse than
 * teaching the first useful move; what must not happen is teaching it under the
 * "out through its matching gate" caption. So the move and its wording change
 * together, and the hand is never made to claim an exit it is not performing.
 *
 * No `exit: true`: this drag is not going into a gate, so the renderer's cap
 * still applies and a long slide stays a legible gesture.
 */
function clearPathDemo(state) {
    const search = clearableFree(state.blocks, state.puzzle.exits, state.puzzle.gridSize, {
        maxNodes: TUTORIAL_NODE_BUDGET,
        // Module 63 — the demo must not teach a drag into a shut gate.
        ...(state.puzzle.plates ? { plates: state.puzzle.plates } : {}),
    });
    const first = search.firstMove;
    if (!first)
        return null;
    return {
        gesture: 'drag',
        blockId: first.blockId,
        direction: first.direction,
        captionKey: 'blockjam.tutorial.clearPath',
    };
}
/**
 * The moments, in priority order. Only one hand is ever on screen, so when two
 * would apply at once the earlier entry wins; the loser stays unseen and gets
 * its turn on a later board.
 */
/**
 * Module 63 (WP-63.E3) — the drag that parks a block on an UNPOWERED plate: the
 * first block, in the first direction, whose full slide ends with one of its
 * cells over a dark plate. Asked of the model's own slide, so the hand never
 * demonstrates a park the board would refuse.
 */
function plateParkDemo(state) {
    const { blocks, puzzle } = state;
    const plates = puzzle.plates ?? [];
    const powered = poweredPlateIds(blocks, plates);
    const dark = plates.filter((p) => !powered.has(p.id));
    if (dark.length === 0)
        return null;
    // Module 77 (D1) — a block of the plate's GATE colour is the one block that
    // must LEAVE through that gate, so parking it is the losing move; the hand
    // demonstrated exactly that on Expert puzzle 1 (and on ~1 in 4 powered
    // boards). Only a different-colour park is demonstrated. A board where only
    // the gate's own colour can reach a plate returns null, which leaves the key
    // unseen (`_maybeShowTutorial`) so the lesson waits for a board where the
    // demonstrated move is not the losing one.
    for (const block of blocks) {
        for (const direction of ALL_DIRECTIONS) {
            const slid = slideFullyAgainst(block, direction, occupiedKeysExcept(blocks, block.id), puzzle.gridSize);
            if (!slid)
                continue;
            const covers = occupiedCells(slid);
            const parked = dark.filter((p) => covers.some((c) => c.row === p.cell.row && c.col === p.cell.col));
            if (parked.length === 0)
                continue;
            if (parked.every((p) => p.gateColorId !== block.colorId))
                return { gesture: 'drag', blockId: block.id, direction };
        }
    }
    return null;
}
export const TUTORIAL_TRIGGERS = [
    {
        key: 'level1',
        captionKey: 'blockjam.tutorial.dragToGate',
        /**
         * Module 68 (WP-68.3) — the PUZZLE ordinal, not the effective level.
         *
         * This read `state.puzzle.levelNumber === 1` from Module 41 until now, and
         * Module 65 quietly made that a hole: a player whose first game is
         * Intermediate starts at level 26 and Expert at level 61, so the one hand
         * that demonstrates the core gesture never fired for them — and since
         * WP-68.1 they get no unlock popup on that board either. Re-keyed onto
         * `puzzle`, exactly as WP-65.F1 re-keyed the power-up lessons onto the
         * number the player actually sees.
         *
         * Still once EVER, not once per track: `level1` is a persisted seen-key, so
         * entering Beginner after Expert does not replay it.
         */
        appliesAt: (_config, _state, puzzle) => puzzle === 1,
        // The caption says "out through its matching gate", so the hand must go out
        // through a matching gate. See `exitDemo` for why the solver's move is not
        // that, and `clearPathDemo` for what happens when no such move exists.
        resolve: (state) => exitDemo(state) ?? clearPathDemo(state),
    },
    {
        key: 'arrows',
        captionKey: 'blockjam.tutorial.arrowsAxis',
        // Gated on the BOARD, not only on the configured level. `arrowsUnlockLevel`
        // is when arrows start appearing, and the generator is free to leave them off
        // a later board — teaching the mechanic on a board that is not showing it is
        // the same mistake as a hand pointing into a wall.
        appliesAt: (config, state) => state.puzzle.levelNumber >= config.unlocks.arrowsUnlockLevel &&
            state.blocks.some((b) => movementConstraintOf(b) !== 'free'),
        resolve: (state) => {
            // The lesson is the CONSTRAINT, so the demonstrated move is one of the two
            // directions the block's own axis allows — never a solver move, which would
            // happily pick some other, unconstrained block and teach nothing about
            // arrows at all.
            for (const block of state.blocks) {
                const constraint = movementConstraintOf(block);
                if (constraint === 'free')
                    continue;
                const axis = constraint === 'horizontalOnly' ? ['right', 'left'] : ['down', 'up'];
                for (const direction of axis) {
                    if (canStep(block, direction, state.blocks, state.puzzle.gridSize)) {
                        return { gesture: 'drag', blockId: block.id, direction };
                    }
                }
            }
            return null;
        },
    },
];
/**
 * Module 52 (WP-52.5) — one lesson per configured power-up, in unlock order.
 *
 * ## The BUTTON, and only the button
 *
 * The Hammer lesson used to resolve a ghost TAP on the most-jammed block, on the
 * reasoning that "a hammer is for the block that is in the way". Browser-checked
 * against the caption, that is the wrong half of the lesson: the sentence is
 * "Tap Hammer, **then** tap a blocked piece", so a hand that opens on a block
 * demonstrates step two while step one — the button the player has never pressed
 * and has just been told they own — sits under the dim, unlit and unexplained.
 * The reference shot (`Bugs/PowerUpUnlockTutorialReference.jpeg`) shows the fix
 * and it is the obvious one: spotlight the button, put the hand on the button.
 *
 * Step two needs no hand for any of them. Arming already puts `ArmBanner` on
 * screen with that power-up's own prompt, which is the same instruction at the
 * moment it becomes true; Rainbow, which has no target to pick, gets
 * `RainbowActiveNotice` instead — "every gate takes any colour / slide a block
 * out to close them", i.e. what it did and how it ends. Between the caption here
 * and that strip, all three of the brief's requirements are answered on the beat
 * each becomes true.
 *
 * ## Deterministic, in the sense that matters
 *
 * `resolve` is unconditional, so unlike a board gesture it can never silently
 * resolve to nothing: `appliesAt` has already established the power-up is
 * unlocked, and the bar renders every configured power-up from level 1
 * (WP-44.2), so the target is always on screen. Every player meets the same
 * lesson at the same level, pointing at the same control.
 *
 * ## Ordering
 *
 * Sorted by unlock level, and appended AFTER the board moments above.
 * `pendingTutorial` takes the first unseen applicable trigger, so a player who
 * somehow arrives at level 35 without having seen Rainbow's lesson gets
 * Rainbow's first — the one they were given first — and Hammer's on the next
 * board. Ties are broken by id so the order is total and does not depend on
 * object key iteration.
 */
/**
 * Module 63 (WP-63.E3) — the two coached beats of a powered gate, in the words
 * the mechanic needs (the user's own play-test found the trace "a connected
 * dotted line" of no obvious meaning): the PLATE is a switch, the LINE is a
 * wire carrying power to the SHUTTER, and the gate stays open only while the
 * plate is held down. Board gestures through the existing coach, each a
 * persisted once-ever key like every other tutorial moment — never a
 * boot-scoped flag (the Module 52 lesson).
 *
 * `poweredPlate` shows first, on the first board with a dark plate a block can
 * reach; `poweredHold` shows once a plate is lit and some block can leave —
 * the moment the player is about to learn, by doing, that leaving the plate
 * shuts the gate. Two beats rather than the plan's four: the two extra beats
 * would have been captions over the same board with no gesture to teach, and
 * the words they would have carried are in these two.
 */
export const POWERED_TRIGGERS = [
    {
        key: 'poweredPlate',
        captionKey: 'blockjam.tutorial.poweredPlate',
        appliesAt: (config, state) => state.puzzle.levelNumber >= config.unlocks.poweredGatesUnlockLevel &&
            (state.puzzle.plates?.length ?? 0) > 0 &&
            poweredPlateIds(state.blocks, state.puzzle.plates).size === 0,
        resolve: (state) => plateParkDemo(state),
    },
    {
        key: 'poweredHold',
        captionKey: 'blockjam.tutorial.poweredHold',
        appliesAt: (config, state) => state.puzzle.levelNumber >= config.unlocks.poweredGatesUnlockLevel &&
            poweredPlateIds(state.blocks, state.puzzle.plates).size > 0,
        resolve: (state) => exitDemo(state),
    },
];
export function powerupTriggers(config, track) {
    // Module 65 (WP-65.F1/F3) — measured against the PUZZLE ordinal, the number
    // on the lock pill, and held until the board carries what the power-up acts
    // on (`powerupOnBoard`), exactly as `encounteredUnlocks` holds the popup.
    // Module 77 (D4) — the unlock puzzle comes from `unlockPuzzleFor`, and ties
    // (every power-up at puzzle 1 on Expert) break on the SHARED table, so the
    // order stays Rainbow → Hammer → Surge → Colour → Widener on every track.
    //
    // User, 2026-09-28 — NO power-up lessons on a track that unlocks every
    // power-up at once (Expert). It assumes the player knows the tools (D4), and
    // queuing five dimmed coach steps one per puzzle (the once-per-puzzle limit
    // is session memory, so each re-entry to puzzle 1 served the next) blocked
    // play for exactly the players it was meant to trust. The buttons' FREE
    // pills, the try-it glow and the tap-to-read description still teach them.
    if (track && config.tracks.find((t) => t.id === track)?.powerupUnlocks === 'all')
        return [];
    return (Object.entries(config.unlocks.powerups)
        // Module 92 — a retired power-up has no lesson (it has no button).
        .filter(([id, at]) => POWERUP_LESSONS[id] !== undefined &&
        typeof at === 'number' &&
        !isPowerupRetired(config, id))
        .map(([id, base]) => [id, unlockPuzzleFor(config, track, id) ?? base, base])
        .sort(([aId, aAt, aBase], [bId, bAt, bBase]) => aAt - bAt || aBase - bBase || aId.localeCompare(bId))
        .map(([id, unlockPuzzle]) => ({
        key: `powerup:${id}`,
        captionKey: POWERUP_LESSONS[id],
        appliesAt: (_config, state, puzzle) => puzzle >= unlockPuzzle && powerupOnBoard(id, state),
        resolve: () => ({ gesture: 'button', powerupId: id }),
    })));
}
/**
 * The tutorial moment this board should show, or null.
 *
 * `resolve` is deliberately NOT called here: whether the board can actually act
 * the gesture out is the renderer's answer as much as the model's (a hand is
 * refused for a move the block cannot make), so the caller resolves, offers it
 * to the board, and only marks the key seen if the board took it.
 */
export function pendingTutorial(config, state, seen = [], 
// WP-65.F1 — the puzzle ordinal the power-up lessons are keyed on. A caller
// with no track (fixtures, the Studio) is on a single ladder where it and the
// level agree, which is what the default says.
puzzle = state.puzzle.levelNumber, opts = {}) {
    const already = new Set(seen);
    // WP-52.5 — the board moments are a fixed list; the power-up lessons are built
    // from the config that owns the unlock levels, so the two can never disagree
    // about when a power-up arrives.
    const all = [
        ...TUTORIAL_TRIGGERS,
        ...POWERED_TRIGGERS,
        ...(opts.powerupLessons === false ? [] : powerupTriggers(config, opts.track)),
    ];
    return all.find((t) => !already.has(t.key) && t.appliesAt(config, state, puzzle)) ?? null;
}
/** The seen set with `key` added — order-stable, no duplicates. */
export function markTutorialSeen(seen, key) {
    return seen.includes(key) ? [...seen] : [...seen, key];
}
/**
 * The persisted seen-set for a save, migrating the Module 41 shape on the way.
 *
 * WP-41.8 stored a single `seenTutorial: boolean`, which could only ever mean
 * "the level-1 hand has been shown" — so that is exactly what it migrates to.
 * The migration is one-way and one-time: `_commitState()` writes only
 * `seenTutorials` from here on, so a save passes through this once and is then
 * in the new shape forever.
 *
 * The trap worth naming: a legacy save with `seenTutorial: false` (or absent)
 * must migrate to an EMPTY set, not to `['level1']`. Getting that backwards
 * silently deletes the tutorial for every player who had not reached it yet.
 */
export function migrateSeenTutorials(saved) {
    if (saved?.seenTutorials)
        return [...saved.seenTutorials];
    return saved?.seenTutorial === true ? ['level1'] : [];
}
